import { F0Box, F0Select } from '@factorialco/f0-react'
import {
  AtsInsightsJobPostingsFilterDocument,
  AtsInsightsMetricsFilterFragment,
} from 'generated/resources/graphql'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import DatePicker from 'modules/core/components/Filter/filters/DatePicker'
import MultipleChoice from 'modules/core/components/Filter/filters/MultipleChoice'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  useMetricsFilters,
} from 'modules/ats/pages/InsightsPage/MetricsFiltersProvider'

interface Props {
  jobs: AtsInsightsMetricsFilterFragment[]
}

type JobPostingFilterNode = {
  id: string
  title: string
  team?: { id: string; name: string } | null
}

const getTeamFilterOptions = (jobs: AtsInsightsMetricsFilterFragment[]) => {
  return uniqBy(
    jobs.flatMap((job) => (job.team ? [{ label: job.team.name ?? '', value: job.team.id }] : [])),
    (job) => job.value
  )
}

export const MetricsFilters = ({ jobs }: Props) => {
  const { filters, setFilters } = useMetricsFilters()
  const teamOptions = getTeamFilterOptions(jobs)

  const teamIdsForQuery = filters.teamIds.length > 0 ? filters.teamIds : undefined

  const jobPostingSource = useGraphqlDataSource(
    {
      document: AtsInsightsJobPostingsFilterDocument,
      paginationType: 'infinite-scroll',
      perPage: 25,
      fetchPolicy: 'cache-first',
      search: { enabled: true },
      mapVariables: ({ search }) => ({
        title: search || undefined,
        teamIds: teamIdsForQuery,
      }),
      mapData: (data): JobPostingFilterNode[] => data.ats.jobPostingsConnection.nodes,
      mapPagination: (data) => ({
        total: data.ats.jobPostingsConnection.nodes.length,
        cursor: data.ats.jobPostingsConnection.pageInfo.endCursor ?? undefined,
        hasNextPage: data.ats.jobPostingsConnection.pageInfo.hasNextPage,
      }),
    },
    [teamIdsForQuery]
  )

  return (
    <F0Box display="flex" gap="md" flexDirection="row" alignItems="center" flexWrap="wrap">
      <DatePicker
        label={i18n.t('ats.insights_page.filters.active_dates')}
        value={[filters.startDateRange, filters.endDateRange]}
        onValueChange={(dateRange) => {
          if (!dateRange) {
            setFilters({ startDateRange: DEFAULT_START_DATE, endDateRange: DEFAULT_END_DATE })
            return
          }

          setFilters({ startDateRange: dateRange[0], endDateRange: dateRange[1] })
        }}
        mode="range"
        minDate={moment('2020-01-01')} // there was no ATS before 2020
        maxDate={moment()}
        dateFormat="ui_dmy_long"
      />

      <MultipleChoice<string>
        label={i18n.t('ats.insights_page.filters.team')}
        value={filters.teamIds}
        onValueChange={(teamIds = []) => {
          track(trackingEvents.ats.insightsTeamsFilter)
          setFilters({ teamIds, atsJobPostingIds: [] })
        }}
        options={teamOptions}
      />

      <F0Box minWidth="52">
        <F0Select<string, JobPostingFilterNode>
          label={i18n.t('ats.insights_page.filters.job_opening')}
          placeholder={i18n.t('ats.insights_page.filters.job_opening')}
          hideLabel
          name="jobPostingFilter"
          multiple
          showSearchBox
          source={jobPostingSource}
          mapOptions={(item: JobPostingFilterNode) => ({
            label: item.title,
            value: item.id,
          })}
          value={filters.atsJobPostingIds}
          onChange={(atsJobPostingIds: string[]) => {
            track(trackingEvents.ats.insightsJobPostingsFilter)
            setFilters({ atsJobPostingIds })
          }}
          clearable
        />
      </F0Box>
    </F0Box>
  )
}
